import { fr } from '@codegouvfr/react-dsfr';
import { Button } from '@codegouvfr/react-dsfr/Button';
import Tooltip from '@codegouvfr/react-dsfr/Tooltip';
import { useGetEtablissementPrincipal } from '@common/hooks/etablissement';
import {
  EtablissementProjection,
  EtablissementTableauOfaDto,
  OrganismeDetailsDto,
  OrganismeStatutType,
  Sorter,
  useDeleteOrganismeEtablissementMutation,
  useGetOrganismeEtablissementsQuery,
  useLazyGetOrganismeEtablissementQuery,
} from '@common/services';
import { StatutDeclaration } from '@common/services/etablissement';
import { ConfirmationModal, FcPaginator, FcTable, FcTableResult, getModalById } from '@common/ui';
import { SearchTextFilter } from '@common/ui/filters';
import { Panel } from '@common/ui/panel';
import React, { useEffect, useRef, useState } from 'react';
import { EditEtablissementInfoModal } from '../modals/EditEtablissementInfoModal';
import { EtablissementInfoModal } from '../modals/EtablissementInfoModal';
import { ETABLISSEMENTS_TRIE_DEFAUT } from '../trie/ETABLISSEMENTS_TRIE_DEFAUT';

const etablissementInfoModal = getModalById('etablissement-info-modal');
const editEtablissementInfoModal = getModalById('etablissement-edit-info-modal');
const deleteEtablissementConfirmationModal = getModalById('etablissement-delete-confirmation-modal');

export const MyOfaEstablishmentsPanel = ({ organisme }: MyOfaEstablishmentsPanelProps) => {
  const organismeId = organisme.id;

  const [searchFilter, setSearchFilter] = useState<string>('');
  const [sorter, setSorter] = useState<Sorter | null>(null);
  const [page, setPage] = useState<number>(0);

  const [isAddMode, setIsAddMode] = useState<boolean>(false);
  const [confirmationModalDescription, setConfirmationModalDescription] = useState<string>('');
  const etablissementToDeleteIdRef = useRef<number | null>(null);

  const { etablissementPrincipal } = useGetEtablissementPrincipal();

  const { data: response } = useGetOrganismeEtablissementsQuery({
    id: organismeId,
    projection: EtablissementProjection.TABLEAU_OFA,
    page,
    sorters: (sorter) ? [sorter] : ETABLISSEMENTS_TRIE_DEFAUT,
    filters: { search: searchFilter },
  }, {
    skip: !organismeId,
    refetchOnMountOrArgChange: true,
  });

  const [getOrganismeEtablissement, { data: etablissement }] = useLazyGetOrganismeEtablissementQuery();
  const [deleteOrganismeEtablissement] = useDeleteOrganismeEtablissementMutation();

  const handleAdd = () => {
    setIsAddMode(true);
    editEtablissementInfoModal.open();
  };

  const handleEdit = async (etablissementId: number) => {
    if (etablissement?.id !== etablissementId) await getOrganismeEtablissement({
      projection: EtablissementProjection.INFO,
      id: organismeId,
      etablissementId,
    }).unwrap();

    setIsAddMode(false);
    editEtablissementInfoModal.open();
  };

  const handleViewInfo = async (etablissementId: number) => {
    if (etablissement?.id !== etablissementId) await getOrganismeEtablissement({
      projection: EtablissementProjection.INFO,
      id: organismeId,
      etablissementId,
    }).unwrap();

    setIsAddMode(false);
    etablissementInfoModal.open();
  };

  const handleDelete = (etablissementId: number, denominationUsuelle: string) => {
    setConfirmationModalDescription(`Vous êtes sur le point de supprimer l'établissement ${denominationUsuelle}.`);
    etablissementToDeleteIdRef.current = etablissementId;
    deleteEtablissementConfirmationModal.open();
  };

  const handleConfirmationDeletion = () => {
    deleteOrganismeEtablissement({ id: organismeId, etablissementId: etablissementToDeleteIdRef.current });
    deleteEtablissementConfirmationModal.close();
  };

  // Permet de retourner à la page précédente dans le cas où on supprime tous les établissements de la page actuelle
  useEffect(() => {
    if (response?.content.length === 0 &&
      response?.pageable.pageNumber > 0
    ) {
      setPage(page - 1);
    }
    // on ajoute uniquement response et non page pour éviter les dépendence cycliques et la boucle infinie
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [response]);

  return (
    <Panel title="Établissements"
           cornerButton={{
             iconId: 'fr-icon-add-line',
             onClick: () => handleAdd(),
             disabled: (organisme.statut === OrganismeStatutType.DEPOT_BLOQUE),
           }}>

      <div className={'mb-6'}>
        <FcTableResult count={response?.totalElements}
                       mapper={(count: number) => `${count} établissement${count > 1 ? 's' : ''}`} />
      </div>

      <div className={'flex mt-6'}>
        <SearchTextFilter onSearchChange={setSearchFilter}
                          minLength={3}
                          debounceTime={500}
                          emitIfEmpty />
      </div>

      <div className="separator my-4" />

      <div className={'flex flex-col gap-6'}>

        <FcTable<EtablissementTableauOfaDto>
          columnsConfig={[
            {
              header: { title: '' },
              field: 'principal',
              render: (principal: boolean) => (principal) && (
                <Tooltip title="Établissement principal" kind="hover">
                  <i className={fr.cx('fr-icon-home-4-fill') + ' color-primary ml-4'} />
                </Tooltip>
              ),
            },
            {
              header: { title: 'SIRET', sorting: true },
              field: 'siret',
            },
            {
              header: { title: 'Dénomination usuelle', sorting: true },
              field: 'denominationUsuelle',
              render: (denominationUsuelle, element) => (
                <span className={'cursor-pointer color-primary underline'}
                      onClick={() => handleViewInfo(element.id)}>{denominationUsuelle}</span>
              ),
            },
            {
              header: { title: 'NDA', sorting: true },
              field: 'nda',
            },
            {
              header: { title: 'UAI', sorting: true },
              field: 'uai',
            },
            {
              header: { title: '' },
              field: 'id',
              maxWidth: '150px',
              displayOnHover: true,
              render: (id, element) => {
                const isDeleteBlockedByDeclaration = ([StatutDeclaration.DEPOSEE, StatutDeclaration.PARTIELLE] as StatutDeclaration[]).includes(element.statutDeclaration);
                const isDeleteDisabled = element.principal || isDeleteBlockedByDeclaration;

                const tooltipTitle = element.principal
                  ? 'Un établissement principal ne peut pas être supprimé. Veuillez désigner un autre établissement comme principal afin de pouvoir supprimer celui-ci.'
                  : isDeleteBlockedByDeclaration
                    ? 'Il y a une déclaration sur cet établissement.'
                    : '';

                return (
                  <div className="flex gap-2">
                    <Button
                      iconId="fr-icon-edit-fill"
                      className={'color-primary'}
                      priority="tertiary no outline"
                      onClick={() => handleEdit(id)}
                      title={'Modifier l\'établissement'}
                    />

                    <Tooltip
                      title={tooltipTitle}
                      kind="hover"
                      style={{ display: isDeleteDisabled ? 'unset' : 'none' }}
                    >
                      <span>
                        <Button
                          iconId="fr-icon-delete-fill"
                          className={!isDeleteDisabled ? 'color-primary' : ''}
                          disabled={isDeleteDisabled}
                          priority="tertiary no outline"
                          onClick={() => handleDelete(id, element.denominationUsuelle)}
                          title={!isDeleteDisabled ? 'Supprimer l\'établissement' : ''}
                        />
                      </span>
                    </Tooltip>
                  </div>
                );
              },
            }]}
          rowConfig={{ selectionConfig: { fieldId: 'id' } }}
          sorter={sorter}
          onSorterChange={setSorter}
          data={response?.content ?? []} />

        {(response?.totalPages > 1) && (
          <div className={'flex flex-col items-center'}>
            <FcPaginator page={response?.pageable.pageNumber ?? 0}
                         totalPages={response?.totalPages ?? 0}
                         onPageChange={setPage} />
          </div>
        )}
      </div>

      <EtablissementInfoModal dsfrModal={etablissementInfoModal}
                              etablissement={etablissement ?? {}} />

      <EditEtablissementInfoModal dsfrModal={editEtablissementInfoModal}
                                  etablissementPrincipal={etablissementPrincipal}
                                  organisme={organisme}
                                  isAddMode={isAddMode}
                                  etablissement={(isAddMode) ? {} : (etablissement ?? {})} />

      <ConfirmationModal dsfrModal={deleteEtablissementConfirmationModal}
                         description={confirmationModalDescription}
                         subDescription="Êtes-vous certain(e) de vouloir continuer ?"
                         confirmProps={{ onClick: () => handleConfirmationDeletion() }} />
    </Panel>
  );
};

interface MyOfaEstablishmentsPanelProps {
  organisme: OrganismeDetailsDto;
  withFilesFilters?: boolean;
  withFilesColumns?: boolean;
}